import React, { useEffect, useRef, useState } from "react";
import { CalendarOff, Plus } from "lucide-react";
import { ConsultationsTable } from "pages/patient/PatientConsultationsPage/components/ConsultationsTable/ConsultationsTable";

import { AppointmentsList } from "components/AppointmentsList/AppointmentsList";
import { OutOfRosterNotice } from "components/AppointmentsList/components/OutOfRosterNotice/OutOfRosterNotice";
import { AppVersion } from "components/AppVersion/AppVersion";
import { MedicalHistory } from "components/PatientDetailCardWithOverview/components/PatientOverviewModal/components/MedicalHistory/MedicalHistory";
import { Button } from "components/ui/Button";
import { PatientDetailCard } from "features/patients/components/PatientDetailCard/PatientDetailCard";
import { useMedicalHistory } from "features/patients/hooks/useMedicalHistory";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useOpenPatientSummaryPage } from "utils/openPatientSummaryPage";

import { PrescriptionTasks } from "./components/PrescriptionTasks";
import { Tasks } from "./components/Tasks";
import s from "./HomePage.module.css";
import { useScheduleAutoSelect } from "./hooks/useScheduleAutoSelect";

const DEFAULT_MAX_HEIGHT = "calc(100dvh - 400px)";

const HomePage = () => {
  const { data: me } = useStaffInfo();

  // Compute the roster gate up-front so we can disable the auto-select
  // hook for users without a calendar / staff member. Without this gate
  // useAppointments is called with an empty selectedDoctors filter,
  // which the backend treats as "all doctors" — auto-select then picks
  // a random patient from another doctor's day and surfaces it in the
  // middle Patient Information panel.
  const rosterValue =
    me?.staffMember?.roster ??
    me?.staffMember?.permissions?.roster ??
    me?.roster;
  const isOutOfRoster = rosterValue === false;
  const hasStaffMember = !!me?.staffMember?.id;
  const autoSelectEnabled = hasStaffMember && !isOutOfRoster;

  const {
    setScheduleDate,
    selectedPatient,
    selectedAppointment,
    selectPatientManually,
  } = useScheduleAutoSelect({
    doctorId: me?.staffMember?.id,
    enabled: autoSelectEnabled,
  });

  const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false);
  const [appointmentsMaxHeight, setAppointmentsMaxHeight] =
    useState(DEFAULT_MAX_HEIGHT);
  const patientInfoRef = useRef(null);
  const tasksRef = useRef(null);
  const appointmentsContainerRef = useRef(null);

  useEffect(() => {
    let overhead = 0;
    let cleanupFn = () => {};

    // Measure overhead (header + calendar + padding above scroll area) after first
    // render, when appointments scroll container is at its default max height.
    // overhead = appointments column height − default scroll max height
    requestAnimationFrame(() => {
      const defaultMax = window.innerHeight - 400;
      if (appointmentsContainerRef.current) {
        overhead = Math.max(
          appointmentsContainerRef.current.clientHeight - defaultMax,
          0
        );
      }

      const update = () => {
        const currentDefaultMax = window.innerHeight - 400;
        const patientHeight = patientInfoRef.current?.clientHeight ?? 0;
        const tasksHeight = tasksRef.current?.clientHeight ?? 0;
        const rightColMax = Math.max(patientHeight, tasksHeight);
        const newScrollMax = Math.max(
          rightColMax - overhead,
          currentDefaultMax
        );
        setAppointmentsMaxHeight(`${newScrollMax}px`);
      };

      const observer = new ResizeObserver(update);
      if (patientInfoRef.current) observer.observe(patientInfoRef.current);
      if (tasksRef.current) observer.observe(tasksRef.current);

      window.addEventListener("resize", update);
      update();

      cleanupFn = () => {
        observer.disconnect();
        window.removeEventListener("resize", update);
      };
    });

    return () => cleanupFn();
  }, []);

  const openPatientSummaryPage = useOpenPatientSummaryPage();
  const { data: medicalHistory } = useMedicalHistory(selectedPatient?.id);
  const hasMedicalHistory = (medicalHistory ?? []).length > 0;

  return (
    <>
      <div className="dashBcontbody alliedhome">
        <div className={s.wrapper}>
          <div className={s.appointmentsPatientContainer}>
            <div ref={appointmentsContainerRef} className={s.appointmentsContainer} style={{ gap: "0px" }}>
              <div className={s.summaryHeading}>
                <h2 className={s.summaryTitle}>My Appointments</h2>
                <Button
                  variant="primary"
                  endIcon={Plus}
                  onClick={() => setShowAddAppointmentModal(true)}
                >
                  Add appointment
                </Button>
              </div>
              {isOutOfRoster ? (
                <OutOfRosterNotice />
              ) : (
                <AppointmentsList
                  isSmall={true}
                  maxHeight={appointmentsMaxHeight}
                  doctorId={me?.staffMember?.id}
                  noFilters={true}
                  className={s.appointmentsList}
                  onDateChange={(date) => setScheduleDate(date)}
                  showAddAppointmentModal={showAddAppointmentModal}
                  setShowAddAppointmentModal={setShowAddAppointmentModal}
                  onAppointmentClick={(appointment) => {
                    const consultation = appointment?.meeting?.consultation;
                    selectPatientManually(
                      consultation?.patient || null,
                      consultation || null
                    );
                  }}
                />
              )}
            </div>

            <div className={s.divider} />

            <div ref={patientInfoRef} style={{ alignSelf: "start" }}>
              <div className={s.summaryHeading}>
                <h2 className={s.summaryTitle}>Patient Information</h2>
                {selectedPatient && (
                  <Button
                    variant="primary"
                    onClick={() =>
                      openPatientSummaryPage(
                        selectedPatient.id,
                        `${selectedPatient.firstName ?? ""} ${
                          selectedPatient.surname ?? ""
                        }`
                      )
                    }
                  >
                    Open consultation
                  </Button>
                )}
              </div>

              {selectedPatient ? (
                <>
                  <PatientDetailCard
                    patientId={selectedPatient?.id}
                    reasonForVisit={selectedAppointment?.reasonDescription}
                    showReasonForVisit
                    clickableName
                  />
                  {hasMedicalHistory && (
                    <>
                      <div
                        className={s.summaryHeading}
                        style={{ marginTop: "24px" }}
                      >
                        <h2 className={s.summaryTitle}>Medical History</h2>
                      </div>
                      <MedicalHistory patientId={selectedPatient.id} />
                    </>
                  )}
                  <div
                    className={s.summaryHeading}
                    style={{ marginTop: "24px" }}
                  >
                    <h2 className={s.summaryTitle}>Recent consultations</h2>
                  </div>
                  <ConsultationsTable
                    patientIdVal={selectedPatient.id}
                    itemsPerPage={5}
                  />
                </>
              ) : (
                <div className={s.noPatientBox}>
                  <div className={s.noPatientState}>
                    <CalendarOff size={56} className={s.noPatientIcon} />
                    <p className={s.noPatientTitle}>
                      No appointments for this day
                    </p>
                    <p className={s.noPatientText}>
                      Select a day with scheduled appointments to view patient
                      details. Patient information is only available when an
                      appointment is selected.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className={s.divider} />

            <div ref={tasksRef} style={{ alignSelf: "start" }}>
              <Tasks selectedPatient={selectedPatient} />
              <div style={{ marginTop: "24px" }}>
                <PrescriptionTasks selectedPatient={selectedPatient} />
              </div>
            </div>
          </div>
        </div>

        <AppVersion className="versionData" />
      </div>
    </>
  );
};

export { HomePage };
